import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, Modal, TextArea, TextField} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';
import {createToast} from '../../../../shared/components/toasts/another-toast/toaster';
import {BASELINE_TEMPLATE_NAME_TAKEN} from '../../../../shared/util/util';
import {CommitMutation} from '../../../../../mutations/CommitMutation';
import {TOAST_DURATION_MS} from '../baselineTemplateFormat';
import {DESCRIPTION_MAX_LENGTH, nameError} from '../form/baselineTemplateDraft';
import DuplicateBaselineTemplateMutation from './DuplicateBaselineTemplateMutation';

export type DuplicateBaselineTemplateModalPayload = {
	/** The list's BaselineTemplate global id. */
	templateId: string;
	name: string;
	description: string | null;
	/** Every template's name, for the same "already in use" check as create. */
	existingNames: readonly string[];
};

export const showDuplicateBaselineTemplateModal = (props: DuplicateBaselineTemplateModalPayload): void => {
	showModal({type: MODAL_TYPE.DUPLICATE_BASELINE_TEMPLATE, ...props});
};

export const DuplicateBaselineTemplateModal = ({
	templateId,
	name,
	description,
	existingNames,
	closeModal,
}: ModalProps<DuplicateBaselineTemplateModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	const [newName, setNewName] = useState(() => formatMessage({id: 'baseline_templates.duplicate.name_copy'}, {name}));
	const [newDescription, setNewDescription] = useState(description ?? '');
	const [nameErrorId, setNameErrorId] = useState<string | undefined>();
	const [submitting, setSubmitting] = useState(false);
	// Set when the prompt is closed while its request is in flight. The copy is still made; its outcome must
	// then neither close a second time, which would pop whatever modal is underneath, nor update this one.
	const closedWhileSubmitting = useRef(false);

	const close = () => {
		if (submitting) {
			closedWhileSubmitting.current = true;
		}
		closeModal();
	};

	const create = () => {
		const problem = nameError(newName, existingNames);
		setNameErrorId(problem);
		if (problem) {
			return;
		}
		const copyName = newName.trim();
		setSubmitting(true);
		CommitMutation(
			DuplicateBaselineTemplateMutation,
			{id: templateId, name: copyName, description: newDescription.trim() === '' ? null : newDescription.trim()},
			() => {
				if (!closedWhileSubmitting.current) {
					closeModal();
				}
				createToast({
					duration: TOAST_DURATION_MS,
					message: formatMessage({id: 'baseline_templates.toast.duplicated'}, {name: copyName}),
				});
			},
			// No automatic retry: each duplicate makes a copy, so a resend after a lost response would hit the
			// unique name index and report a name the first request had already saved.
			false,
			null,
			(error, onFailure) => {
				if (!closedWhileSubmitting.current) {
					setSubmitting(false);
				}
				if (!closedWhileSubmitting.current && String(error).includes(`${BASELINE_TEMPLATE_NAME_TAKEN}:`)) {
					setNameErrorId('baseline_templates.errors.name_taken');
					return;
				}
				onFailure?.(error);
			}
		);
	};

	const nameErrorText = nameErrorId ? formatMessage({id: nameErrorId}) : undefined;
	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={close} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'baseline_templates.duplicate.title'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="l">
					<TextField
						label={formatMessage({id: 'baseline_templates.details.name'})}
						data-cy="baseline-template-duplicate-name"
						value={newName}
						onValueChange={(value: string) => {
							setNewName(value);
							setNameErrorId(undefined);
						}}
						onBlur={() => setNameErrorId(nameError(newName, existingNames))}
						hasError={!!nameErrorText}
						errorText={nameErrorText}
					/>
					{/* TextArea hands onChange the value, not an event, and needs an onBlur. */}
					<TextArea
						label={formatMessage({id: 'baseline_templates.details.description'})}
						data-cy="baseline-template-duplicate-description"
						isOptional
						height={72}
						maxLength={DESCRIPTION_MAX_LENGTH}
						value={newDescription}
						onChange={(value: string) => setNewDescription(value)}
						onBlur={() => undefined}
					/>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={create} disabled={submitting} dataCy="baseline-template-duplicate-create">
					{formatMessage({id: 'baseline_templates.duplicate.create'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={close} disabled={submitting} dataCy="baseline-template-duplicate-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
